import type {HotelDetails} from '@/api/hoteldetails';
import type {SortParams} from '@/modules/common/context/QueryParams/models';
import type {
  EventAction,
  EventActionCreator,
  EventHandler,
  HotelListEcommerceItem,
} from '@/redux/middleware/googleTagManager/types';
import type {BestOffer} from '@/redux/v1/offers/list/models';

import {OFFER_BOX_EVENT_ORIGIN_CTA_BUTTON} from '@/modules/offers/components/OfferBox/models';
import {
  OFFER_DETAILS_OVERLAY_SECTION_FLIGHT_DETAILS,
  OFFER_DETAILS_OVERLAY_SECTION_OFFER_DETAILS,
} from '@/modules/offers/components/OfferDetailsOverlay/models';
import {EVENT_SELECT_PROMOTION} from '@/redux/middleware/googleTagManager/actionTypes';
import {mapBestOfferType} from '@/redux/middleware/googleTagManager/utils/mapBestOfferType';
import {mapToSortListNameForOffer} from '@/redux/middleware/googleTagManager/utils/mapToSortListNameForOffer';
import {mapOfferEcommerceItem} from '@/redux/middleware/googleTagManager/utils/offerData';
import {waitForGtmDomEvent} from '@/redux/middleware/googleTagManager/utils/waitForGtmDomEvent';
import {selectAvailability} from '@/redux/v1/availability/reducer';

type Payload = {
  hotelId?: string;
  bestOffer: BestOffer;
  hotelDetails: HotelDetails;
  origin: string;
  travellerCount: number;
  sortParams: SortParams;
};

type Action = EventAction<Payload>;

type NullEvent = {
  ecommerce: null;
};

type Event = {
  event: string;
  event_action: string;
  non_interaction: true;
  is_ad: '1 - sponsored' | '0';
  ecommerce: {
    promotion_name: string;
    items: Array<HotelListEcommerceItem>;
  };
};

export const action: EventActionCreator<Action, Payload> = (payload) => ({
  type: EVENT_SELECT_PROMOTION,
  payload,
});

const mapOriginToEventAction = (origin: string) => {
  switch (origin) {
    case OFFER_DETAILS_OVERLAY_SECTION_OFFER_DETAILS: {
      return 'best_offer_clicked_details';
    }
    case OFFER_DETAILS_OVERLAY_SECTION_FLIGHT_DETAILS: {
      return 'best_offer_clicked_details';
    }
    case OFFER_BOX_EVENT_ORIGIN_CTA_BUTTON: {
      return 'best_offer_clicked';
    }
    default: {
      return 'best_offer_clicked';
    }
  }
};

export const handler: EventHandler<
  Action,
  Promise<[NullEvent, Event]>
  // @ts-expect-error ...
> = waitForGtmDomEvent<[NullEvent, Event], Payload>((eventAction, state) => {
  const {hotelId, hotelDetails, bestOffer, origin, travellerCount, sortParams} =
    eventAction.payload;

  return [
    {ecommerce: null},
    {
      event: 'select_promotion',
      event_action: mapOriginToEventAction(origin),
      non_interaction: true,
      is_ad: bestOffer.sponsored ? '1 - sponsored' : '0',
      ecommerce: {
        promotion_name: 'Best offer box',
        items: mapOfferEcommerceItem({
          hotelId,
          travellerCount,
          hotelData: hotelDetails,
          offer: selectAvailability(bestOffer.offerID, state),
          index: 0,
          itemListName: mapToSortListNameForOffer(sortParams.sortBy ?? ''),
          itemBestOfferType: mapBestOfferType(bestOffer.type),
        }),
      },
    },
  ];
});
